import { Component, h, Prop, Host, Element } from '@stencil/core';
import LazyLoad from 'vanilla-lazyload';
import { getImageExtension, getImageMimeType, getWebpImageSrc } from '../../utils/image';
import { ImageDimensions, ImageLoadingType } from './picture-interface';
@Component({
  tag: 'o2-picture',
  styleUrl: 'picture.scss',
  shadow: true,
})
export class O2Picture {
  /**
   * Calculated source picture extension
   */
  private extension: string;

  @Element() el!: HTMLO2PictureElement;

  /**
   * Image source (can be jpe?g, png, gif)
   */
  @Prop({ reflect: true }) src: string;

  /**
   * Image srcset to define multiple image size. It can be multiple lines divided with comma.
   * Example: srcset="
   * /assets/mock/castle/castle-w_200.jpg 200w,
   * /assets/mock/castle/castle-w_520.jpg 520w"
   */
  @Prop() srcset?: string;

  /**
   * Image sizes to define breaking point to show largest image.
   * Example: sizes="(max-width: 1200px) 100vw, 1200px"
   */
  @Prop() sizes?: string;

  /**
   * Image alternative text
   */
  @Prop({ reflect: true }) alt?: string;

  /**
   * Image width
   */
  @Prop({ reflect: true }) width?: string;

  /**
   * Image height
   */
  @Prop({ reflect: true }) height?: string;

  /**
   * Skip webp source format.
   */
  @Prop() skipWebp?: boolean = false;

  /**
   * Component will render as lazy loading by default
   */
  @Prop() loading?: ImageLoadingType = 'lazy';

  /**
   * Get image extension when element is attached
   */
  connectedCallback() {
    this.extension = getImageExtension(this.src);
  }

  /**
   * Init LazyLoad library after render
   */
  componentDidRender() {
    const lazyImage: NodeListOf<HTMLElement> = this.el.shadowRoot.querySelectorAll('.lazy');
    new LazyLoad(
      {
        use_native: false,
      },
      lazyImage,
    );
  }

  /**
   * Returns picture srcset for webp variant
   */
  private getWebpImageSrcset(): string {
    const replaceExtention = new RegExp(this.extension, 'gi');
    return this.srcset.replace(replaceExtention, '.webp');
  }

  /**
   * Returns calculated style for picture based on actual width and height
   */
  private getImageDimensions(): {} {
    const style: ImageDimensions = {};

    if (this.width) {
      style.width = `${this.width}px`;
    }

    if (this.height) {
      style.height = `${this.height}px`;
    }

    return style;
  }

  render() {
    const getProvidedSources = this.srcset ? this.srcset : this.src;
    return (
      <Host style={this.getImageDimensions()}>
        <picture part="picture" class="o2-picture">
          {this.skipWebp ? (
            ''
          ) : (
            <source
              type="image/webp"
              sizes={this.sizes}
              data-srcset={this.srcset ? this.getWebpImageSrcset() : getWebpImageSrc(this.src, this.extension)}
              srcSet={this.srcset ? this.getWebpImageSrcset() : getWebpImageSrc(this.src, this.extension)}
            />
          )}
          <source type={getImageMimeType(this.extension)} sizes={this.sizes} data-srcset={getProvidedSources} srcSet={this.src} />
          <img
            part="image"
            loading={this.loading}
            sizes={this.sizes}
            data-srcset={this.srcset}
            data-src={getProvidedSources}
            alt={this.alt}
            width={this.width}
            height={this.height}
            class="lazy"
            src={this.src}
          />
        </picture>
      </Host>
    );
  }
}
